import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useRef,
    useLayoutEffect,
    useCallback,
} from "react";

interface OnChainAddressProps {
    address: string;
    className?: string;
}

export interface OnChainAddressHandle {
    handleCopy: () => Promise<void>;
}

const ELLIPSIS = "...";

const getFont = (element: HTMLElement): string => {
    const style = window.getComputedStyle(element);
    return `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
};

const truncateMiddleToWidth = (
    text: string,
    maxWidth: number,
    ctx: CanvasRenderingContext2D,
): string => {
    if (maxWidth <= 0) {
        return text;
    }
    if (ctx.measureText(text).width <= maxWidth) {
        return text;
    }

    let low = 0;
    let high = text.length;
    let best = ELLIPSIS;

    while (low <= high) {
        const keep = (low + high) >> 1;
        const front = Math.ceil(keep / 2);
        const back = Math.floor(keep / 2);
        const candidate =
            text.slice(0, front) + ELLIPSIS + text.slice(text.length - back);

        if (ctx.measureText(candidate).width <= maxWidth) {
            best = candidate;
            low = keep + 1;
        } else {
            high = keep - 1;
        }
    }

    return best;
};

const renderColoredAddress = (text: string): React.ReactNode => {
    if (text.length <= 8) {
        return <span>{text}</span>;
    }

    const firstPart = text.slice(0, 4);
    const middlePart = text.slice(4, -4);
    const lastPart = text.slice(-4);

    return (
        <>
            <span>{firstPart}</span>
            <span className="text-brand-color-2">{middlePart}</span>
            <span>{lastPart}</span>
        </>
    );
};

const OnChainAddress = forwardRef<OnChainAddressHandle, OnChainAddressProps>(
    ({ address, className }, ref) => {
        const [copied, setCopied] = useState(false);
        const [displayedAddress, setDisplayedAddress] = useState(address);
        const textRef = useRef<HTMLDivElement>(null);
        const canvasRef = useRef<HTMLCanvasElement | null>(null);

        const handleCopy = async () => {
            try {
                await navigator.clipboard.writeText(address);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            } catch (err) {
                console.error("Failed to copy:", err);
            }
        };

        useImperativeHandle(ref, () => ({
            handleCopy,
        }));

        const recompute = useCallback(() => {
            const element = textRef.current;
            if (!element) {
                return;
            }

            if (!canvasRef.current) {
                canvasRef.current = document.createElement("canvas");
            }
            const ctx = canvasRef.current.getContext("2d");
            if (!ctx) {
                setDisplayedAddress(address);
                return;
            }

            ctx.font = getFont(element);
            setDisplayedAddress(
                truncateMiddleToWidth(address, element.clientWidth, ctx),
            );
        }, [address]);

        useLayoutEffect(() => {
            recompute();

            const element = textRef.current;
            if (!element || typeof ResizeObserver === "undefined") {
                return;
            }

            const observer = new ResizeObserver(() => recompute());
            observer.observe(element);
            return () => observer.disconnect();
        }, [recompute]);

        return (
            <div>
                <div
                    className={`flex items-center gap-2 cursor-pointer ${className || ""} ${copied ? "invisible h-[1px]" : ""}`}
                    onClick={() => !copied && handleCopy()}
                >
                    <div
                        ref={textRef}
                        className="flex-1 min-w-0 overflow-hidden whitespace-nowrap"
                        title={address}
                    >
                        {renderColoredAddress(displayedAddress)}
                    </div>
                    <i
                        className="material-icons text-[18px] shrink-0"
                        title="Copy to clipboard"
                    >
                        file_copy
                    </i>
                </div>
                <div
                    className={`flex items-center gap-2 ${className || ""} ${!copied ? "invisible h-[1px]" : ""}`}
                >
                    <span className="break-words [word-break:break-word]">
                        Copied to clipboard
                    </span>
                    <i className="material-icons text-[18px]">check</i>
                </div>
            </div>
        );
    },
);

OnChainAddress.displayName = "OnChainAddress";

export default OnChainAddress;
